import React, { useState, useEffect, useRef } from "react";
import { Card, Table, Row, Col, Button, Form } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import useAxiosPost from "../../hooks/useAxiosPost";
import { notifyError } from "../toast/Error";
import { notifySuccess } from "../toast/Success";

const AdminOltVlansComponent: React.FC = () => {
  const [selectedOltId, setSelectedOltId] = useState<string>("");
  const [selectedOltName, setSelectedOltName] = useState<string>("");

  // API for getting OLTs
  const [
    getOltsData,
    oltsResponseData,
    oltsError,
    oltsLoading,
    oltsMessage,
    oltsErrorCount,
  ] = useAxiosPost<any>("olt/getAllOltsByCompanyMini");

  // API for getting VLANs
  const [
    getVlansData,
    vlansResponseData,
    vlansError,
    vlansLoading,
    vlansMessage,
    vlansErrorCount,
  ] = useAxiosPost<any>("vlans/getMyOltVlans");

  const [olts, setOlts] = useState<any[]>([]);
  const [vlans, setVlans] = useState<any[]>([]);
  const [selectedOltDetails, setSelectedOltDetails] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredOlts, setFilteredOlts] = useState<any[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load OLTs on component mount
  useEffect(() => {
    getOltsData({});
  }, []);

  // Handle OLT selection
  const handleOltSelection = (oltId: string, oltName: string) => {
    setSelectedOltId(oltId);
    setSelectedOltName(oltName);
    if (oltId && olts && olts.length > 0) {
      // Find the selected OLT details
      const oltDetails = olts.find(
        (olt) => olt && olt.intPkOltID && olt.intPkOltID.toString() === oltId
      );
      setSelectedOltDetails(oltDetails || null);
      getVlansData({ intPkOltID: Number(oltId) });
    } else {
      setVlans([]);
      setSelectedOltDetails(null);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setIsDropdownOpen(true);
        inputRef.current?.focus();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOlts.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOlts.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOlts.length) {
          const olt = filteredOlts[highlightedIndex];
          handleOltSelection(olt.intPkOltID.toString(), olt.oltName);
          setIsDropdownOpen(false);
          setSearchTerm("");
        }
        break;
      case "Escape":
        setIsDropdownOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
        break;
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle OLTs API response
  useEffect(() => {
    if (oltsError !== null && oltsErrorCount !== 0) {
      notifyError(oltsError);
    }
  }, [oltsError, oltsErrorCount]);

  useEffect(() => {
    if (oltsResponseData !== null) {
      const { responseData } = oltsResponseData;
      setOlts(responseData || []);
      setFilteredOlts(responseData || []);
    }
  }, [oltsResponseData]);

  // Filter OLTs based on search term
  useEffect(() => {
    if (!olts || olts.length === 0) {
      setFilteredOlts([]);
      return;
    }

    if (searchTerm.trim() === "") {
      setFilteredOlts(olts);
    } else {
      const filtered = olts.filter((olt) => {
        if (!olt) return false;
        return (
          (olt.oltName &&
            olt.oltName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (olt.brand &&
            olt.brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (olt.model &&
            olt.model.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (olt.host &&
            olt.host.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      });
      setFilteredOlts(filtered);
    }
    setHighlightedIndex(-1); // Reset highlighted index when search changes
  }, [searchTerm, olts]);

  // Handle VLANs API response
  useEffect(() => {
    if (vlansError !== null && vlansErrorCount !== 0) {
      notifyError(vlansError);
    }
  }, [vlansError, vlansErrorCount]);

  useEffect(() => {
    if (vlansResponseData !== null) {
      const { responseData } = vlansResponseData;
      setVlans(responseData || []);
    }
  }, [vlansResponseData]);

  return (
    <>
      <style>
        {`
          .hover-bg-light:hover {
            background-color: #f8f9fa !important;
          }
        `}
      </style>
      <ToastContainer />
      <Row className="justify-content-center">
        <Col xs lg="12" md="12" xl="12" className="p-5">
          <Card className="p-5">
            <Card.Title className="text-center olt-heading fw-700 pt-3">
              Admin OLT VLANs Management
            </Card.Title>

            {/* OLT Selection */}
            <Row className="mt-4 mb-4">
              <Col md={12}>
                <Card className="bg-light">
                  <Card.Header className="fw-bold">Select OLT</Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Choose an OLT:</Form.Label>
                      <div ref={dropdownRef} className="position-relative">
                        <div
                          className="form-control d-flex align-items-center justify-content-between"
                          style={{
                            cursor: "pointer",
                            minHeight: "38px",
                            backgroundColor: oltsLoading ? "#e9ecef" : "white",
                          }}
                          onClick={() => {
                            if (!oltsLoading) {
                              setIsDropdownOpen(!isDropdownOpen);
                              inputRef.current?.focus();
                            }
                          }}
                          onKeyDown={handleKeyDown}
                          tabIndex={0}
                        >
                          <div className="d-flex align-items-center flex-grow-1">
                            {selectedOltId ? (
                              <span>
                                {selectedOltName} (
                                {selectedOltDetails?.brand || "N/A"} -{" "}
                                {selectedOltDetails?.model || "N/A"})
                              </span>
                            ) : (
                              <span className="text-muted">
                                {oltsLoading
                                  ? "Loading OLTs..."
                                  : "Search and select an OLT..."}
                              </span>
                            )}
                          </div>
                          <div className="d-flex align-items-center">
                            {searchTerm && (
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 me-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clearSearch();
                                }}
                                style={{ fontSize: "12px" }}
                              >
                                ✕
                              </Button>
                            )}
                            <span className="text-muted">
                              {isDropdownOpen ? "▲" : "▼"}
                            </span>
                          </div>
                        </div>

                        {isDropdownOpen && (
                          <div
                            className="position-absolute w-100 bg-white border border-top-0 rounded-bottom"
                            style={{
                              zIndex: 1000,
                              maxHeight: "200px",
                              overflowY: "auto",
                              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                            }}
                          >
                            <div className="p-2 border-bottom">
                              <Form.Control
                                ref={inputRef}
                                type="text"
                                placeholder="Type to search OLTs..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="border-0"
                                style={{ boxShadow: "none" }}
                                autoFocus
                              />
                            </div>
                            <div>
                              {filteredOlts.length > 0 ? (
                                filteredOlts.map((olt: any, index: number) => (
                                  <div
                                    key={olt?.intPkOltID || index}
                                    className={`px-3 py-2 cursor-pointer ${
                                      index === highlightedIndex
                                        ? "bg-primary text-white"
                                        : "hover-bg-light"
                                    }`}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      if (olt?.intPkOltID && olt?.oltName) {
                                        handleOltSelection(
                                          olt.intPkOltID.toString(),
                                          olt.oltName
                                        );
                                        setIsDropdownOpen(false);
                                        setSearchTerm("");
                                      }
                                    }}
                                    onMouseEnter={() =>
                                      setHighlightedIndex(index)
                                    }
                                  >
                                    <div className="fw-bold">
                                      {olt?.oltName || "Unknown OLT"}
                                    </div>
                                    <small
                                      className={
                                        index === highlightedIndex
                                          ? "text-white-50"
                                          : "text-muted"
                                      }
                                    >
                                      {olt?.brand || "N/A"} -{" "}
                                      {olt?.model || "N/A"}{" "}
                                      {olt?.host && `(${olt.host})`}
                                    </small>
                                  </div>
                                ))
                              ) : (
                                <div className="px-3 py-2 text-muted text-center">
                                  {searchTerm
                                    ? `No OLTs found matching "${searchTerm}"`
                                    : "No OLTs available"}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {searchTerm && filteredOlts.length > 0 && (
                        <small className="text-muted">
                          Found {filteredOlts.length} OLT(s) matching your
                          search
                        </small>
                      )}
                    </Form.Group>
                    {oltsLoading && (
                      <div className="text-center">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">
                            Loading OLTs...
                          </span>
                        </div>
                        <p className="mt-2">Loading OLTs...</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* OLT Profile Details */}
            {selectedOltDetails && (
              <Row className="mt-3 mb-4">
                <Col md={12}>
                  <Card className="bg-light">
                    <Card.Header className="fw-bold">
                      OLT Profile Details
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={3}>
                          <strong>Brand:</strong>{" "}
                          {selectedOltDetails.brand || "N/A"}
                        </Col>
                        <Col md={3}>
                          <strong>Model:</strong>{" "}
                          {selectedOltDetails.model || "N/A"}
                        </Col>
                        <Col md={3}>
                          <strong>OLT Name:</strong>{" "}
                          {selectedOltDetails.oltName || "N/A"}
                        </Col>
                        <Col md={3}>
                          <strong>IP Address:</strong>{" "}
                          {selectedOltDetails.host || "N/A"}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* VLANs Table */}
            {selectedOltId && (
              <Row className="mt-3">
                <Col md={12}>
                  <Card>
                    <Card.Header className="fw-bold">
                      VLANs for {selectedOltName}
                    </Card.Header>
                    <Card.Body>
                      {vlansLoading ? (
                        <div className="text-center">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">
                              Loading VLANs...
                            </span>
                          </div>
                          <p className="mt-2">Loading VLANs...</p>
                        </div>
                      ) : (
                        <Table bordered>
                          <thead>
                            <tr>
                              <th className="table-header-standard">
                                VLAN Name
                              </th>
                              <th className="table-header-standard">
                                VLAN Value
                              </th>
                              <th className="table-header-standard">
                                ISP Name
                              </th>
                              <th className="table-header-standard">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vlans.length > 0 ? (
                              vlans.map((vlan: any, index: number) => {
                                return (
                                  <tr key={vlan.intPkVlanID || index}>
                                    <td>{vlan.vlanName || "N/A"}</td>
                                    <td>{vlan.vlan || "N/A"}</td>
                                    <td>{vlan.ispName || "N/A"}</td>
                                    <td>
                                      <span
                                        className={`badge ${
                                          vlan.enable === 1
                                            ? "bg-success"
                                            : "bg-danger"
                                        }`}
                                      >
                                        {vlan.enable === 1
                                          ? "Enabled"
                                          : "Disabled"}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan={5} className="text-center">
                                  No VLANs found for this OLT
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default AdminOltVlansComponent;
